// Stock control — item settings, stock take, batches & expiry, reports, QC at
// receipt, packing BOMs and tank summary (client, 2026-10-24).
// Server: src/api-inventory/stock-control.js, packing-boms.js, material-inward.js, tanks.js.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { MaterialInward, TankStatus } from './useMaterials';

export type ItemType = 'finished_good' | 'raw_material' | 'packaging' | 'consumable' | 'stock_item';
export type StockStatus = 'ok' | 'reorder' | 'critical' | 'negative' | 'out';

export interface StockItem {
  _id: string;
  name: string;
  sku: string;
  itemType: ItemType;
  stockUnit: string;
  reorderPoint: number;
  safetyStock: number;
  leadTimeDays: number;
  density: number;
  shelfLifeMonths: number;
  qcRequired: boolean | null;
  onHand: number;
  value: number;
  status: StockStatus;
  suggestedOrder: number;
}

export interface StockItemsResponse {
  items: StockItem[];
  total: number;
  page: number;
  pageSize: number;
  counts: { all: number; negative: number; reorder: number; critical: number; out: number; inStock: number };
  itemTypes: Record<ItemType, string>;
  units: string[];
  canEdit: boolean;
}

export function useStockItems(filters: { q: string; type: string; status: string; page: number }) {
  return useQuery({
    queryKey: ['inventory', 'stock-items', filters],
    queryFn: async () => (await api.get<StockItemsResponse>('/api/inventory/stock-control/items', {
      params: { q: filters.q || undefined, type: filters.type !== 'all' ? filters.type : undefined, status: filters.status, page: filters.page },
    })).data,
  });
}

export function useSaveStockSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<StockItem> & { id: string }) => (
      (await api.put<{ item: StockItem }>(`/api/inventory/stock-control/items/${id}/settings`, body)).data.item
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'stock-items'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'overview'] });
    },
  });
}

export interface StockTakeLine { productId: string; itemName: string; bookQty: number; countedQty: number; difference: number; batchNumber: string; newItem?: boolean }
export interface StockTakeRecord {
  _id: string; number: string; dateKey: string; note: string; lines: StockTakeLine[];
  increased: number; decreased: number; unchanged: number; postedByName: string; createdAt: string;
}

export async function postStockTake(body: { date: string; note: string; lines: { productId: string; countedQty: number; batchNumber?: string }[] }, dryRun: boolean) {
  const { data } = await api.post<{ lines?: StockTakeLine[]; stockTake?: StockTakeRecord }>(
    `/api/inventory/stock-control/stock-take${dryRun ? '?dryRun=true' : ''}`, body,
  );
  return data;
}

export function useStockTakes() {
  return useQuery({
    queryKey: ['inventory', 'stock-takes'],
    queryFn: async () => (await api.get<{ stockTakes: StockTakeRecord[] }>('/api/inventory/stock-control/stock-takes')).data.stockTakes,
  });
}

export interface Batch {
  productId: string; itemName: string; stockUnit: string; lotNumber: string; quantity: number;
  receivedAt: string | null; expiryDate: string | null; daysToExpiry: number | null; reference: string;
}

export function useBatches(expiringDays: number) {
  return useQuery({
    queryKey: ['inventory', 'batches', expiringDays],
    queryFn: async () => (await api.get<{ batches: Batch[] }>('/api/inventory/stock-control/batches', { params: expiringDays ? { expiringDays } : {} })).data.batches,
  });
}

export function useStockOverview() {
  return useQuery({
    queryKey: ['inventory', 'overview'],
    refetchInterval: 120_000,
    queryFn: async () => (await api.get<{ negative: number; belowReorder: number; qcPending: number; expiringSoon: number }>('/api/inventory/stock-control/overview')).data,
  });
}

export interface TypeSummary { items: number; inStock: number; negative: number; belowReorder: number; value: number }

export function useStockSummary() {
  return useQuery({
    queryKey: ['inventory', 'stock-summary'],
    queryFn: async () => (await api.get<{ byType: Record<ItemType, TypeSummary>; itemTypes: Record<ItemType, string> }>('/api/inventory/stock-control/reports/summary')).data,
  });
}

export interface ConsumptionRow { productId: string; itemName: string; stockUnit: string; quantity: number; value: number; issues: number; lastIssuedAt: string }
export function useConsumptionReport(from: string, to: string) {
  return useQuery({
    queryKey: ['inventory', 'consumption', from, to],
    queryFn: async () => (await api.get<{ rows: ConsumptionRow[] }>('/api/inventory/stock-control/reports/consumption', { params: { from, to } })).data.rows,
  });
}

export interface SupplierInwardRow { supplierName: string; receipts: number; items: number; value: number; rejected: number; lastReceipt: string }
export function useInwardBySupplier(from: string, to: string) {
  return useQuery({
    queryKey: ['inventory', 'inward-by-supplier', from, to],
    queryFn: async () => (await api.get<{ rows: SupplierInwardRow[] }>('/api/inventory/stock-control/reports/inward-by-supplier', { params: { from, to } })).data.rows,
  });
}

// ── QC at receipt ────────────────────────────────────────────────────────────

export interface QcBody {
  id: string; result: 'accepted' | 'partly_accepted' | 'rejected'; acceptedQty?: string; density?: string; viscosity40?: string;
  appearance?: string; waterPpm?: string; flashPointC?: string; remarks?: string; coaUrl?: string; coaName?: string;
}

export function useRecordQc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: QcBody) => (
      (await api.post<{ inward: MaterialInward; stockWarning?: string }>(`/api/inventory/material-inward/${id}/qc`, body)).data
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'material-inward'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'overview'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'balances'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'batches'] });
    },
  });
}

export async function uploadFile(file: File) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<{ url: string }>('/api/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data.url;
}

// ── Packing BOMs ─────────────────────────────────────────────────────────────

export interface BomComponent {
  productId: string | null; itemName: string; category: 'raw_material' | 'packaging_material' | 'consumables';
  qtyPerUnit: number; unit: string; source: 'warehouse' | 'tank';
}
export interface PackingBom { _id: string; finishedProductId: string; finishedName: string; components: BomComponent[]; notes: string; updatedByName: string; updatedAt: string }

export function usePackingBoms() {
  return useQuery({
    queryKey: ['inventory', 'packing-boms'],
    queryFn: async () => (await api.get<{ boms: PackingBom[]; canEdit: boolean; categories: Record<string, string> }>('/api/inventory/packing-boms')).data,
  });
}

export function useSaveBom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id?: string; finishedProductId?: string; finishedName?: string; components: Partial<BomComponent>[]; notes?: string }) => (
      id ? (await api.put<{ bom: PackingBom }>(`/api/inventory/packing-boms/${id}`, body)).data.bom
        : (await api.post<{ bom: PackingBom }>('/api/inventory/packing-boms', body)).data.bom
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'packing-boms'] }),
  });
}

export function useDeleteBom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/inventory/packing-boms/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory', 'packing-boms'] }),
  });
}

export type ExpandedLine = BomComponent & { quantity: number; onHand: number | null };
/** The finished item picked from the list (productId) or just typed (name). */
export async function expandBom(item: { productId: string | null; itemName: string }, units: number) {
  const params = item.productId ? { productId: item.productId, units } : { name: item.itemName.trim(), units };
  const { data } = await api.get<{ lines: ExpandedLine[] }>('/api/inventory/packing-boms/expand', { params });
  return data.lines;
}

// ── Tank summary ─────────────────────────────────────────────────────────────

export interface TankSummary {
  tanks: { tankCode: string; capacityKl: number | null; product: string; status: TankStatus | null; raw: string; dateKey: string | null; kl: number | null; fillPct: number | null }[];
  grades: { product: string; tanks: string[]; kl: number; unknownTanks: number }[];
  capacityMissing: number;
}

export function useTankSummary() {
  return useQuery({
    queryKey: ['inventory', 'tank-register', 'summary'],
    queryFn: async () => (await api.get<TankSummary>('/api/inventory/tanks/summary')).data,
  });
}
